/**
 * Sentry instrumentation — must be imported as the very first line of main.tsx.
 *
 * Isolating Sentry.init() in its own module guarantees that the Vite bundler
 * fully executes this file before any other module (including vendor chunks for
 * React and @sentry/react) runs. This prevents the production ESM crash:
 *   "Cannot read properties of undefined (reading 'forwardRef')"
 *
 * See: https://docs.sentry.io/platforms/javascript/guides/react/#install
 */
import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import * as Sentry from "@sentry/react";

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // Tunnel only works in dev (Vite middleware). In production, send directly to Sentry.
    ...(import.meta.env.DEV ? { tunnel: "/api/sentry-tunnel" } : {}),
    sendDefaultPii: true,
    enableLogs: true,

    // ── Integrations ──────────────────────────────────────────────
    integrations: [
      // React Router v6-aware tracing — maps raw URLs to route patterns
      // (e.g. /corporate/abc-123/vendors/xyz → /corporate/:id/vendors/:profileId)
      // so Sentry groups performance data by route instead of unique URL.
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        // Drop realtime/websocket fetch spans within traced transactions
        shouldCreateSpanForRequest: (url) =>
          !url.includes("/realtime/v1/") && !url.includes("/health"),
      }),
      // Browser JS Self-Profiling — activates only when Document-Policy: js-profiling header is present
      Sentry.browserProfilingIntegration(),
      // Capture failed fetch/XHR (4xx/5xx) with request+response context
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [[400, 599]],
      }),
      // Extract non-standard error properties (PostgrestError.code, .details, .hint)
      Sentry.extraErrorDataIntegration(),
      // Session replay on errors
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
      // Forward console.log/warn/error to Sentry Logs
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
      // Lazy-loaded user feedback widget
      Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: "system",
        isNameRequired: true,
        isEmailRequired: true,
      }),
      // Drop errors originating exclusively from browser extensions / injected scripts
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ["b2b-campaign-engine"],
        behaviour: "drop-error-if-exclusively-contains-third-party-frames",
      }),
    ],

    // ── Error Filtering ──────────────────────────────────────────
    ignoreErrors: [
      // Browser / layout noise
      "ResizeObserver loop",
      "ResizeObserver loop completed with undelivered notifications",
      // Chunk loading (network flake / deploy race)
      "Loading chunk",
      "Failed to fetch dynamically imported module",
      "ChunkLoadError",
      // Browser extension / third-party injection
      "fb_xd_fragment",
      "__gCrWeb",
      "instantSearchSDKJSBridgeClearHighlight",
      // Safari-specific WebSocket noise
      "The operation couldn't be completed",
      // Intentional cancellations (user navigation, React strict-mode unmount)
      "AbortError",
      // Business validation — corporate missing required financial inputs
      "MissingFinancialInputError",
      "MissingCardProgramInputError",
    ],

    // ── URL Filtering ────────────────────────────────────────────
    allowUrls: [
      /https?:\/\/app\.b2bactivate\.com/,
      /https?:\/\/(.*\.)?supabase\.co/,
      /localhost/,
    ],

    // ── Sampling ─────────────────────────────────────────────────
    tracesSampler: ({ name, attributes, inheritOrSampleWith }) => {
      // Always capture auth transactions
      if (name?.includes("auth") || name?.includes("/auth")) return 1.0;
      // Always capture campaign send/launch operations
      if (name?.includes("campaign") && (name?.includes("send") || name?.includes("launch"))) return 1.0;
      // Drop health-check / heartbeat polling
      if (name?.includes("healthcheck") || name?.includes("heartbeat")) return 0;
      // Drop Supabase realtime subscription pings
      if (name?.includes("realtime") || name?.includes("websocket")) return 0;
      // Higher sample rate for page loads (important for Web Vitals)
      if (attributes?.["sentry.op"] === "pageload") return 0.5;
      // Default: inherit from parent trace, or fall back to 10%
      return inheritOrSampleWith(0.1);
    },

    tracePropagationTargets: ["localhost", /^https:\/\/.*\.supabase\.co/],
    profileSessionSampleRate: 1.0,  // Profile 100% of sessions (reduce before scale)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,

    // ── Event Hooks ───────────────────────────────────────────────
    beforeSend(event) {
      // Scrub auth tokens from breadcrumb data
      if (event.breadcrumbs) {
        for (const crumb of event.breadcrumbs) {
          if (crumb.data) {
            for (const key of Object.keys(crumb.data)) {
              if (/token|authorization|apikey|secret|password/i.test(key)) {
                crumb.data[key] = "[Filtered]";
              }
            }
          }
        }
      }
      return event;
    },

    beforeSendLog(log) {
      // Drop debug/trace logs in production
      if (log.level === "debug" || log.level === "trace") return null;
      // Redact sensitive attributes
      if (log.attributes) {
        for (const key of Object.keys(log.attributes)) {
          if (/password|secret|token|authorization|apikey/i.test(key)) {
            log.attributes[key] = "[Filtered]";
          }
        }
      }
      return log;
    },

    beforeSendMetric(metric) {
      // Redact any sensitive attribute keys that accidentally make it into a metric
      if (metric.attributes) {
        for (const key of Object.keys(metric.attributes)) {
          if (/password|secret|token|authorization|apikey/i.test(key)) {
            metric.attributes[key] = "[Filtered]";
          }
        }
      }
      return metric;
    },
  });

  // Global scope attributes for structured log correlation
  Sentry.getGlobalScope().setAttributes({
    "service.name": "b2b-campaign-engine",
    "deployment.environment": import.meta.env.MODE,
  });
}
